import { z } from "zod";
import { DischargeReason, MhaStatus } from "../../patientState/PatientState.js";
import { AuditTrailEntryType } from "../AuditTrailEntryType.js";
// Patient-state audit `data` schemas. See `registry.ts` for the audit-data leniency
// contract these follow.
//
// `status` / `dischargeReason` reuse the `MhaStatus` / `DischargeReason` enums
// (`z.enum`, not the usual `z.string()`) because the activity log reads them as typed
// values; a stored value outside the current enum falls to the imprecise read.
export const updatePatientStateWithManualEventDataSchema = z.object({
    status: z.enum(MhaStatus),
    dischargeReason: z.enum(DischargeReason).nullish(),
    dischargeDateTime: z.string().nullish(),
    effectiveDateTime: z.string().nullish(),
    startDateTime: z.string().nullish(),
    expiryDateTime: z.string().nullish(),
    section136ExtensionHours: z.number().nullish(),
    previousPlaceOfSafetyName: z.string().nullish(),
});
export const updatePatientStateHospitalManualDataSchema = z.object({
    status: z.enum(MhaStatus),
    hospitalName: z.string().nullable(),
    previousPlaceOfSafetyName: z.string().nullish(),
});
export const updatePatientStateAutomaticallyDataSchema = z.object({
    status: z.enum(MhaStatus),
    initialStartDateTime: z.string().nullish(),
    startDateTime: z.string().nullish(),
    expiryDateTime: z.string().nullish(),
    numberOfRenewals: z.number().nullish(),
    effectiveDateTime: z.string().nullish(),
    formTemplateId: z.string().nullish(),
    dischargeDateTime: z.string().nullish(),
    forceName: z.string().nullish(),
    officerName: z.string().nullish(),
});
// Recorded when the police report a place-of-safety arrival earlier than the one
// a Section 136 was started from, pulling the detention clock back. The
// detention is the same one throughout — only the window it is measured over
// moves — so this is deliberately not a status change, and `startDateTime` is
// the corrected legal start rather than when the correction was applied.
export const section136ClockCorrectedDataSchema = z.object({
    initialStartDateTime: z.string().nullish(),
    startDateTime: z.string().nullish(),
    expiryDateTime: z.string().nullish(),
    effectiveDateTime: z.string().nullish(),
    forceName: z.string().nullish(),
    officerName: z.string().nullish(),
});
// Recorded when a patient enters dual MHA status (an active Section 136
// alongside an active non-136 detention). `coexistingStatus` is the Section 136
// side; `existingStatus` is the substantive non-136 detention. Both are required
// by the schema — the writer always supplies them — and the activity log builds
// the "Coexisting MHA statuses detected" message from them, falling back to a
// generic message if an older/partial row is missing them.
export const dualMhaStatusDetectedDataSchema = z.object({
    coexistingStatus: z.enum(MhaStatus),
    existingStatus: z.enum(MhaStatus),
});
// Recorded when one side of a dual MHA status is cleared and a single detention
// remains (e.g. the Section 136 is resolved while a Section 2 continues).
// `remainingStatus` is the surviving detention; the activity log builds the
// "Coexisting MHA status cleared" message from it, falling back to a generic
// message if an older/partial row is missing it.
export const coexistingMhaStatusClearedDataSchema = z.object({
    remainingStatus: z.enum(MhaStatus),
});
export const automaticallyExpirePatientStateDataSchema = z.object({
    status: z.enum(MhaStatus),
    effectiveDateTime: z.string().nullish(),
    expiryDateTime: z.string(), // required: read unconditionally
});
export const manuallyOverridePatientStateExpiryDateDataSchema = z.object({
    status: z.enum(MhaStatus),
    expiryDateTime: z.string().nullish(),
    reason: z.string().nullish(),
    reasonDescription: z.string().nullish(),
});
// Records that an MHA admin manually extended a Section 136 detention's end date.
// `expiryDateTime` is the resulting (extended) end date; `section136ExtensionHours`
// is the number of hours added on top of the standard 24h. Distinct from a
// form-signed extension and from the expiry override used for other sections.
export const manuallyExtendPatientStateSection136DataSchema = z.object({
    status: z.literal(MhaStatus.Section136),
    expiryDateTime: z.string().nullish(),
    section136ExtensionHours: z.number().nullish(),
});
// Records that an MHA admin cleared a previously-applied Section 136 manual extension,
// resetting the detention's end date back to the standard 24h default.
// `expiryDateTime` is the resulting (reset) end date.
export const manuallyClearPatientStateSection136ExtensionDataSchema = z.object({
    status: z.literal(MhaStatus.Section136),
    expiryDateTime: z.string().nullish(),
});
// Patient identity / merge / demographics / background.
export const createPatientDataSchema = z.object({
    source: z
        .enum(["aspire", "rio", "tpp", "pds", "bulkImport", "vision", "generic"])
        .nullish(),
});
export const updatePatientDataSchema = z.object({
    patientFieldChanges: z.record(z.string(), z.string()),
});
// mergePatient and unmergePatient share a shape.
export const patientMergeDataSchema = z.object({
    mergeReason: z.string().nullish(),
    reason: z.string().nullish(), // legacy rows wrote `reason` instead of `mergeReason`
    reasonDescription: z.string().nullish(),
    mergedPatient: z.string().nullish(),
    destinationPatient: z.string().nullish(),
});
export const createExternalPatientLinkDataSchema = z.object({
    source: z.string().nullish(),
});
export const createPatientDemographicsPullEventDataSchema = z.object({
    reason: z.string().nullish(),
    processed: z.boolean().nullish(),
});
export const updatePatientDemographicsPullEventDataSchema = z.object({
    processed: z.boolean(),
    externalSystemType: z.string(),
    differences: z.array(z.object({ type: z.string() })).nullish(),
});
export const createPatientDemographicsAlertDataSchema = z.object({
    type: z.enum(["deceased", "invalidated"]),
});
export const clearPatientDemographicsAlertsDataSchema = z.object({
    type: z.string().nullish(),
    reason: z.string().nullish(),
});
export const patientSearchReasoningDataSchema = z.object({
    reason: z.string(),
    reasonDescription: z.string().nullish(),
    workItemType: z.string().nullish(),
    incidentId: z.string().nullish(),
});
export const patientSearchReasoningSensitiveDataSchema = z.object({
    reason: z.string(),
    reasonDescription: z.string().nullish(),
    restricted: z.boolean(),
    deceasedDateTime: z.string().nullable(),
    deathNotificationStatus: z.string().nullable(),
    workItemType: z.string().nullish(),
    incidentId: z.string().nullish(),
});
export const adminPatientDetailsLookupDataSchema = z.object({
    reason: z.string().nullish(),
    reasonDescription: z.string().nullish(),
    enteredPatientId: z.string().nullish(),
    rootPatientId: z.string().nullish(),
    postcodeWithheld: z.boolean().nullish(),
    /** Which nodes of the merge tree carried the restriction that withheld it. */
    restrictedPatientIds: z.array(z.string()).nullish(),
    outcome: z.string().nullish(),
});
export const searchPatientIndexesDataSchema = z.object({
    searchType: z.string().nullish(),
    searchVariant: z.string().nullish(),
});
export const bulkPatientImportRowProcessedDataSchema = z.object({
    fileKey: z.string(),
    rowNumber: z.number(),
    token: z.string(),
    userId: z.string(),
    userName: z.string(),
    userEmail: z.string(),
});
export const backgroundTracePatientDataSchema = z.object({
    indicatorStatus: z.string(),
    indicatorText: z.string(),
    traceNeeded: z.boolean(),
});
export const backgroundSyncRioEthnicityDataSchema = z.object({
    success: z.boolean(),
    persisted: z.boolean().nullish(),
    changed: z.boolean().nullish(),
    ethnicityCode: z.string().nullish(),
    ethnicityDisplay: z.string().nullish(),
    reason: z.string().nullish(),
    error: z.string().nullish(),
});
// Shared empty payload.
export const emptyPatientDataSchema = z.object({});
// Audit type → schema entries for this file, composed in ../registry.ts.
export const patientAuditDataRegistry = {
    // Patient state.
    [AuditTrailEntryType.updatePatientStateWithManualEvent]: updatePatientStateWithManualEventDataSchema,
    [AuditTrailEntryType.updatePatientStateHospitalManual]: updatePatientStateHospitalManualDataSchema,
    [AuditTrailEntryType.updatePatientStateAutomatically]: updatePatientStateAutomaticallyDataSchema,
    [AuditTrailEntryType.section136ClockCorrected]: section136ClockCorrectedDataSchema,
    [AuditTrailEntryType.dualMhaStatusDetected]: dualMhaStatusDetectedDataSchema,
    [AuditTrailEntryType.coexistingMhaStatusCleared]: coexistingMhaStatusClearedDataSchema,
    [AuditTrailEntryType.automaticallyExpirePatientState]: automaticallyExpirePatientStateDataSchema,
    [AuditTrailEntryType.manuallyOverridePatientStateExpiryDate]: manuallyOverridePatientStateExpiryDateDataSchema,
    [AuditTrailEntryType.manuallyExtendPatientStateSection136]: manuallyExtendPatientStateSection136DataSchema,
    [AuditTrailEntryType.manuallyClearPatientStateSection136Extension]: manuallyClearPatientStateSection136ExtensionDataSchema,
    // Identity / merge / demographics / background.
    [AuditTrailEntryType.createPatient]: createPatientDataSchema,
    [AuditTrailEntryType.updatePatient]: updatePatientDataSchema,
    [AuditTrailEntryType.mergePatient]: patientMergeDataSchema,
    [AuditTrailEntryType.unmergePatient]: patientMergeDataSchema,
    [AuditTrailEntryType.createExternalPatientLink]: createExternalPatientLinkDataSchema,
    [AuditTrailEntryType.createPatientDemographicsPullEvent]: createPatientDemographicsPullEventDataSchema,
    [AuditTrailEntryType.updatePatientDemographicsPullEvent]: updatePatientDemographicsPullEventDataSchema,
    [AuditTrailEntryType.createPatientDemographicsAlert]: createPatientDemographicsAlertDataSchema,
    [AuditTrailEntryType.clearPatientDemographicsAlerts]: clearPatientDemographicsAlertsDataSchema,
    [AuditTrailEntryType.patientSearchReasoning]: patientSearchReasoningDataSchema,
    [AuditTrailEntryType.patientSearchReasoningSensitive]: patientSearchReasoningSensitiveDataSchema,
    [AuditTrailEntryType.adminPatientDetailsLookup]: adminPatientDetailsLookupDataSchema,
    [AuditTrailEntryType.searchPatientIndexes]: searchPatientIndexesDataSchema,
    [AuditTrailEntryType.bulkPatientImportRowProcessed]: bulkPatientImportRowProcessedDataSchema,
    [AuditTrailEntryType.backgroundTracePatient]: backgroundTracePatientDataSchema,
    [AuditTrailEntryType.backgroundSyncRioEthnicity]: backgroundSyncRioEthnicityDataSchema,
    [AuditTrailEntryType.createSuggestedPatientMerge]: emptyPatientDataSchema,
    [AuditTrailEntryType.actionSuggestedPatientMerge]: emptyPatientDataSchema,
    [AuditTrailEntryType.patientSupersededNhsNumberUpdated]: emptyPatientDataSchema,
    [AuditTrailEntryType.getPatient]: emptyPatientDataSchema,
    [AuditTrailEntryType.getPatientTimeline]: emptyPatientDataSchema,
    [AuditTrailEntryType.adminManuallyQueueBackgroundTracePatient]: emptyPatientDataSchema,
    [AuditTrailEntryType.synchronisePatientWithPds]: emptyPatientDataSchema,
    [AuditTrailEntryType.createPatientBulkImportToken]: emptyPatientDataSchema,
    [AuditTrailEntryType.revokePatientBulkImportToken]: emptyPatientDataSchema,
    [AuditTrailEntryType.usePatientBulkImportToken]: emptyPatientDataSchema,
    // Patient list.
    [AuditTrailEntryType.removePatientFromPatientList]: emptyPatientDataSchema,
};
//# sourceMappingURL=patient.js.map